import React, { useState, useEffect, useRef } from 'react'
import { 
  Plus,
  Download,
  ClipboardList,
  FileText,
  Clock,
  CheckCircle,
  XCircle,
  DollarSign
} from 'lucide-react'
import DataTable from '../../components/DataTable/DataTable'
import Breadcrumb from '../../components/Common/Breadcrumb'
import ConfirmationModal from '../../components/ConfirmationModal/ConfirmationModal'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import purchaseRequisitionService from '../../services/purchaseRequisitionService'
import '../../components/ConfirmationModal/ConfirmationModal.css'

const PurchaseRequisition = () => {
  const navigate = useNavigate()
  const [data, setData] = useState([])
  const [loading, setLoading] = useState(true)
  const [stats, setStats] = useState({
    totalRequisitions: 0,
    draftRequisitions: 0,
    pendingApproval: 0,
    approvedRequisitions: 0,
    rejectedRequisitions: 0,
    totalEstimatedAmount: 0
  })
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [deleteItem, setDeleteItem] = useState(null)
  const hasLoadedRef = useRef(false)
  const hasShownEmptyToastRef = useRef(false)

  useEffect(() => {
    if (!hasLoadedRef.current) {
      hasLoadedRef.current = true
      loadData()
    }
  }, [])

  const loadData = async () => {
    try {
      setLoading(true)
      const [requisitionsResponse, statsResponse] = await Promise.all([
        purchaseRequisitionService.getAllPurchaseRequisitions(),
        purchaseRequisitionService.getPurchaseRequisitionStats()
      ])
      
      if (requisitionsResponse.success) {
        const transformedData = (requisitionsResponse.data || []).map(requisition => ({
          ...requisition,
          id: requisition._id
        }))
        setData(transformedData)
        if (requisitionsResponse.data?.length === 0 && !hasShownEmptyToastRef.current) {
          hasShownEmptyToastRef.current = true
          toast.info('No purchase requisition records found. Create your first requisition!')
        }
      } else {
        toast.error(requisitionsResponse.message || 'Failed to load purchase requisitions')
      }
      
      if (statsResponse.success) {
        setStats(statsResponse.data)
      } else {
        toast.error(statsResponse.message || 'Failed to load statistics')
      }
    } catch (error) {
      console.error('Error loading purchase requisition records:', error)
      if (error.response?.status === 401) {
        toast.error('Authentication required. Please log in again.')
      } else if (error.response?.status === 403) {
        toast.error('You do not have permission to view purchase requisitions')
      } else if (!error._errorHandled) {
        toast.error('Failed to load purchase requisition records')
      }
    } finally {
      setLoading(false)
    }
  }

  const formatCurrency = (amount, currency = 'INR') => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 2
    }).format(amount || 0)
  }

  const getStatusBadgeClass = (status) => {
    const statusMap = {
      'Draft': 'inactive',
      'Submitted': 'pending',
      'Pending Approval': 'pending',
      'Approved': 'active',
      'Rejected': 'danger',
      'Cancelled': 'inactive'
    }
    return statusMap[status] || 'inactive'
  }

  const getPriorityBadgeClass = (priority) => {
    const priorityMap = {
      'Low': 'inactive',
      'Medium': 'pending',
      'High': 'warning',
      'Urgent': 'danger'
    }
    return priorityMap[priority] || 'inactive'
  }

  const columns = [
    {
      accessorKey: 'requisitionNumber',
      header: 'Requisition Number',
      cell: ({ getValue }) => (
        <div className="service-code-cell" style={{ textAlign: 'left' }}>
          <span className="code-text">{getValue()}</span>
        </div>
      ),
    },
    {
      accessorKey: 'requesterName',
      header: 'Requester',
      cell: ({ getValue, row }) => (
        <div className="service-description-cell" style={{ textAlign: 'left' }}>
          <span className="description-text">{getValue() || '-'}</span>
          {row.original.requesterDepartment && (
            <div style={{ fontSize: '12px', color: 'var(--color-text-secondary, #5B6478)' }}>{row.original.requesterDepartment}</div>
          )}
        </div>
      ),
    },
    {
      accessorKey: 'requisitionDate',
      header: 'Requisition Date',
      cell: ({ getValue }) => {
        const date = getValue()
        return (
          <div style={{ textAlign: 'center' }}>
            {date ? new Date(date).toLocaleDateString() : '-'}
          </div>
        )
      },
    },
    {
      accessorKey: 'requiredDate',
      header: 'Required Date',
      cell: ({ getValue }) => {
        const date = getValue()
        return (
          <div style={{ textAlign: 'center' }}>
            {date ? new Date(date).toLocaleDateString() : '-'}
          </div>
        )
      },
    },
    {
      accessorKey: 'priority',
      header: 'Priority',
      cell: ({ getValue }) => (
        <div className="active-cell" style={{ textAlign: 'center' }}>
          <span className={`status-badge ${getPriorityBadgeClass(getValue())}`}>
            {getValue() || '-'}
          </span>
        </div>
      ),
    },
    {
      accessorKey: 'financialSummary.totalEstimatedAmount',
      header: 'Estimated Amount',
      cell: ({ row }) => (
        <div style={{ textAlign: 'right', fontWeight: '600' }}>
          {formatCurrency(row.original.financialSummary?.totalEstimatedAmount || 0, row.original.financialSummary?.currency || 'INR')}
        </div>
      ),
    },
    {
      accessorKey: 'approvalWorkflow.status',
      header: 'Status',
      cell: ({ row }) => (
        <div className="active-cell" style={{ textAlign: 'center' }}>
          <span className={`status-badge ${getStatusBadgeClass(row.original.approvalWorkflow?.status)}`}>
            {row.original.approvalWorkflow?.status || '-'}
          </span>
        </div>
      ),
    },
  ]

  const handleAdd = () => {
    navigate('/procurement/purchase-requisition/form')
  }

  const handleEdit = (requisitionData) => {
    navigate('/procurement/purchase-requisition/edit', { state: { requisitionData } })
  }

  const handleView = (requisitionData) => {
    navigate('/procurement/purchase-requisition/view', { state: { requisitionData } })
  }

  const handleDelete = (requisitionData) => {
    setDeleteItem(requisitionData)
    setShowDeleteModal(true)
  }

  const confirmDelete = async () => {
    if (!deleteItem) return

    try {
      toast.info('Deleting purchase requisition...')
      const response = await purchaseRequisitionService.deletePurchaseRequisition(deleteItem._id)
      if (response.success) {
        toast.success('Purchase requisition deleted successfully!', { autoClose: 2000 })
        loadData()
      } else {
        toast.error(response.message || 'Failed to delete purchase requisition')
      }
    } catch (error) {
      console.error('Error deleting purchase requisition:', error)
      if (error.response?.status === 404) {
        toast.error('Purchase requisition not found')
      } else if (error.response?.status === 400) {
        toast.error(error.response.data?.message || 'Can only delete draft requisitions')
      } else if (error.response?.status === 403) {
        toast.error('You do not have permission to delete this purchase requisition')
      } else if (!error._errorHandled) {
        toast.error('Failed to delete purchase requisition. Please try again.')
      }
    } finally {
      setShowDeleteModal(false)
      setDeleteItem(null)
    }
  }

  const cancelDelete = () => {
    setShowDeleteModal(false)
    setDeleteItem(null)
  }

  const handleExport = () => {
    toast.info('Export functionality coming soon!')
  }

  const breadcrumbItems = [
    { label: 'Procurement', path: '/procurement' },
    { label: 'Transactions', path: '/procurement?tab=transactions' },
    { label: 'Purchase Requests', isCurrent: true }
  ]

  return (
    <div className="sales-dashboard">
      <Breadcrumb items={breadcrumbItems} />

      <div className="page-header">
        <div className="page-title">
          <h1>Purchase Requests</h1>
          <p>Employee purchase requests and requisitions</p>
        </div>
        <div className="page-actions">
          <button type="button" className="btn-primary" onClick={handleAdd}>
            <Plus size={16} />
            New Request
          </button>
          <button type="button" className="btn btn-outline-secondary btn-sm" onClick={handleExport}>
            <Download size={16} />
            Export
          </button>
        </div>
      </div>

      <div className="dashboard-metrics-grid">
        <div className="metric-card deals">
          <div className="metric-header">
            <div className="metric-icon">
              <ClipboardList size={16} />
            </div>
            <div className="metric-trend positive">
              <span>Total</span>
            </div>
          </div>
          <div className="metric-value">{stats.totalRequisitions || 0}</div>
          <div className="metric-title">Total Requests</div>
          <div className="metric-subtitle">All purchase requisitions</div>
        </div>

        <div className="metric-card customers">
          <div className="metric-header">
            <div className="metric-icon">
              <FileText size={16} />
            </div>
            <div className="metric-trend neutral">
              <span>Draft</span>
            </div>
          </div>
          <div className="metric-value">{stats.draftRequisitions || 0}</div>
          <div className="metric-title">Draft</div>
          <div className="metric-subtitle">In preparation</div>
        </div>

        <div className="metric-card churn">
          <div className="metric-header">
            <div className="metric-icon">
              <Clock size={16} />
            </div>
            <div className="metric-trend negative">
              <span>Pending</span>
            </div>
          </div>
          <div className="metric-value">{stats.pendingApproval || 0}</div>
          <div className="metric-title">Pending Approval</div>
          <div className="metric-subtitle">Awaiting review</div>
        </div>

        <div className="metric-card revenue">
          <div className="metric-header">
            <div className="metric-icon">
              <CheckCircle size={16} />
            </div>
            <div className="metric-trend positive">
              <span>Approved</span>
            </div>
          </div>
          <div className="metric-value">{stats.approvedRequisitions || 0}</div>
          <div className="metric-title">Approved</div>
          <div className="metric-subtitle">Ready for PO</div>
        </div>

        <div className="metric-card customers">
          <div className="metric-header">
            <div className="metric-icon">
              <XCircle size={16} />
            </div>
            <div className="metric-trend neutral">
              <span>Rejected</span>
            </div>
          </div>
          <div className="metric-value">{stats.rejectedRequisitions || 0}</div>
          <div className="metric-title">Rejected</div>
          <div className="metric-subtitle">Not approved</div>
        </div>

        <div className="metric-card burn">
          <div className="metric-header">
            <div className="metric-icon">
              <DollarSign size={16} />
            </div>
            <div className="metric-trend positive">
              <span>Value</span>
            </div>
          </div>
          <div className="metric-value">{formatCurrency(stats.totalEstimatedAmount || 0)}</div>
          <div className="metric-title">Total Estimated</div>
          <div className="metric-subtitle">All requests combined</div>
        </div>
      </div>

      <div className="data-table-section">
        <DataTable
          data={data}
          columns={columns}
          loading={loading}
          onEdit={handleEdit}
          onView={handleView}
          onDelete={handleDelete}
          enableSelection={false}
          noCard={true}
          className="purchase-requisition-table"
          pageSize={10}
        />
      </div>

      <ConfirmationModal
        isOpen={showDeleteModal}
        onClose={cancelDelete}
        onConfirm={confirmDelete}
        title="Delete Purchase Requisition"
        message={`Are you sure you want to delete requisition "${deleteItem?.requisitionNumber}"? This action cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
        type="danger"
      />
    </div>
  )
}

export default PurchaseRequisition
